import { Switch } from "@headlessui/react";
import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ThreeDot } from "react-loading-indicators";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import { classNames } from "../../../../utils/classNames";
import countries from "../../../../utils/countries";

const pageLabel = {
	title: "Notification Settings",
	save: "Save",
};

const inputLabel = {
	phoneNumber: "Phone Number",
	email: "Email",
	phoneNumberPlaceholder: "Enter phone number",
	emailPlaceholder: "Enter email",
	addPhoneNumber: "Add Phone Number",
	addEmail: "Add Email",
};

const fetchNotificationSettings = async (propertyId) => {
	const response = await apiClient.get(`/addOnsNotification/${propertyId}`);
	return response.data.result.addOnNotifications;
};

const Notification = () => {
	const { selectedPropertyId } = useProperty();

	const [loading, setLoading] = useState(true);
	const [addOnsNotification, setAddOnsNotification] = useState(null);

	const formRef = useRef(null);

	const handleSwitchChange = (index) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];
			updatedSettings[index].enabled = !updatedSettings[index].enabled;
			return updatedSettings;
		});
	};

	const getNotificationSettings = async () => {
		try {
			setLoading(true);

			const settings = await fetchNotificationSettings(selectedPropertyId);

			setAddOnsNotification(settings);
		} catch (error) {
			toast.error("Failed to fetch notification settings. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getNotificationSettings();
	}, [selectedPropertyId]);

	const handleEmailChange = (index, emailIndex, value) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];
			updatedSettings[index].emails[emailIndex] = value;
			return updatedSettings;
		});
	};

	const handleSave = async () => {
		try {
			const saveData = addOnsNotification
				.filter((addOn) => addOn.enabled || addOn._id)
				.map((addOn) => ({
					type: addOn.type,
					enabled: addOn.enabled,
					phoneNumbers: addOn.phoneNumbers,
					countryCode: addOn.countryCode,
					addOnId: addOn.addOnId,
					emails: addOn.emails,
				}));

			saveData.forEach((addOn) => {
				const phoneInput = formRef.current.querySelectorAll(
					`input[name="${addOn.type}PhoneNumber"]`,
				);

				const emailInput = formRef.current.querySelectorAll(
					`input[name="${addOn.type}Email"]`,
				);

				const countryCodeInput = formRef.current.querySelectorAll(
					`select[name="${addOn.type}CountryCode"]`,
				);

				if (phoneInput && countryCodeInput) {
					Array.from(phoneInput).forEach((input) => {
						input.reportValidity();
					});

					Array.from(countryCodeInput).forEach((input) => {
						input.reportValidity();
					});
				}

				if (emailInput) {
					Array.from(emailInput).forEach((input) => {
						input.reportValidity();
					});
				}
			});

			if (!formRef.current.reportValidity()) {
				toast.error("Please fill in all required fields.");
				return;
			}

			await apiClient.put(`/addOnsNotification/${selectedPropertyId}`, {
				addOnNotifications: saveData,
			});

			await getNotificationSettings();

			toast.success("Notification settings saved successfully.");
		} catch (error) {
			toast.error(`${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleCountryCodeChange = (index, phoneIndex, value) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];

			const [, countryCode] = value.split(" ");

			updatedSettings[index].phoneNumbers[phoneIndex].countryCode = countryCode;

			return updatedSettings;
		});
	};

	const handlePhoneNumberChange = (index, phoneIndex, value) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];

			updatedSettings[index].phoneNumbers[phoneIndex].phoneNumber = value;

			return updatedSettings;
		});
	};

	const handleAddPhoneNumber = (index) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];

			updatedSettings[index].phoneNumbers.push({
				phoneNumber: "",
				countryCode: "+1",
			});

			return updatedSettings;
		});
	};

	const handleRemovePhoneNumber = (index, phoneIndex) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];

			updatedSettings[index].phoneNumbers.splice(phoneIndex, 1);

			return updatedSettings;
		});
	};

	const handleAddEmail = (index) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];

			updatedSettings[index].emails.push("");

			return updatedSettings;
		});
	};

	const handleRemoveEmail = (index, emailIndex) => {
		setAddOnsNotification((prevSettings) => {
			const updatedSettings = [...prevSettings];

			updatedSettings[index].emails.splice(emailIndex, 1);

			return updatedSettings;
		});
	};

	return (
		<div className="bg-gray-100 min-h-screen flex justify-center p-6">
			<div
				className="
					bg-white
					rounded-lg
					shadow-lg
					border
					border-gray-200
					flex
					flex-col
					w-full
					max-w-7xl
					h-[calc(100vh-3rem)]
				"
			>
				{/* Sticky Header */}
				<div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm">
					<h2 className="text-2xl font-semibold">{pageLabel.title}</h2>

					{!loading && (
						<button
							type="button"
							className="
								bg-green-500
								hover:bg-green-600
								text-white
								font-medium
								px-5
								py-2
								rounded-md
								shadow-lg
								transition-all
								duration-200
							"
							onClick={handleSave}
						>
							{pageLabel.save}
						</button>
					)}
				</div>

				{/* Scrollable Content */}
				<div className="flex-1 overflow-y-auto p-6">
					{!loading ? (
						<form className="w-full" ref={formRef}>
							<div className="mb-4">
								{addOnsNotification?.map((addOn, i) => (
									<div key={addOn.type} className="flex items-center space-x-4">
										<div className="bg-white shadow-md rounded-lg p-6 w-full mb-6 border border-gray-100">
											<div className="flex items-center justify-between mb-4">
												<h3 className="font-semibold text-lg">{addOn.type}</h3>

												<Switch
													className={classNames(
														addOn.enabled ? "bg-indigo-600" : "bg-neutral-300",
														"relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
													)}
													checked={addOn.enabled}
													onChange={() => handleSwitchChange(i)}
												>
													<span className="sr-only">Use setting</span>

													<span
														aria-hidden="true"
														className={classNames(
															addOn.enabled ? "translate-x-5" : "translate-x-0",
															"pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
														)}
													/>
												</Switch>
											</div>

											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
												{/* Phone Numbers */}
												<div>
													{addOn.phoneNumbers.length > 0 && (
														<label className="block text-sm font-medium text-gray-700 mb-2">
															{inputLabel.phoneNumber}
														</label>
													)}

													{addOn.phoneNumbers.map((p, phoneIndex) => (
														<div
															className="flex items-center justify-center space-x-2 mb-4"
															key={`${addOn.type}PhoneNumber${phoneIndex}`}
														>
															<PhoneInput
																addOn={addOn}
																onCountryCodeChange={(e) =>
																	handleCountryCodeChange(
																		i,
																		phoneIndex,
																		e.target.value,
																	)
																}
																onPhoneNumberChange={(e) =>
																	handlePhoneNumberChange(
																		i,
																		phoneIndex,
																		e.target.value,
																	)
																}
																phoneNumber={p.phoneNumber}
																countryCode={p.countryCode || "+1"}
																disabled={!addOn.enabled}
															/>

															<button
																type="button"
																className="text-red-500"
																onClick={() =>
																	handleRemovePhoneNumber(i, phoneIndex)
																}
															>
																<Trash2 />
															</button>
														</div>
													))}

													<button
														type="button"
														className={classNames(
															"text-blue-500 hover:underline",
															!addOn.enabled
																? "opacity-50 cursor-not-allowed"
																: "",
														)}
														onClick={() => handleAddPhoneNumber(i)}
														disabled={!addOn.enabled}
													>
														{inputLabel.addPhoneNumber}
													</button>
												</div>

												{/* Emails */}
												<div>
													{addOn.emails.length > 0 && (
														<label className="block text-sm font-medium text-gray-700 mb-2">
															{inputLabel.email}
														</label>
													)}

													{addOn?.emails?.map((email, emailIndex) => (
														<div
															className="flex items-center space-x-2 mb-4"
															key={`${addOn.type}Email${emailIndex}`}
														>
															<input
																type="email"
																required
																disabled={!addOn.enabled}
																name={`${addOn.type}Email`}
																className={classNames(
																	"border border-gray-300 rounded-md p-2 w-full text-sm",
																	addOn.enabled
																		? ""
																		: "bg-neutral-200 text-neutral-500",
																)}
																placeholder={inputLabel.emailPlaceholder}
																onChange={(e) =>
																	handleEmailChange(
																		i,
																		emailIndex,
																		e.target.value,
																	)
																}
																value={email}
															/>

															<button
																type="button"
																className="text-red-500"
																onClick={() => handleRemoveEmail(i, emailIndex)}
															>
																<Trash2 />
															</button>
														</div>
													))}

													<button
														type="button"
														className={classNames(
															"text-blue-500 hover:underline",
															!addOn.enabled
																? "opacity-50 cursor-not-allowed"
																: "",
														)}
														onClick={() => handleAddEmail(i)}
														disabled={!addOn.enabled}
													>
														{inputLabel.addEmail}
													</button>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</form>
					) : (
						<div className="flex items-center justify-center py-20">
							<ThreeDot
								width={50}
								height={50}
								color="#4F46E5"
								variant="brick-stack"
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

function PhoneInput({
	addOn,
	onCountryCodeChange,
	onPhoneNumberChange,
	phoneNumber,
	countryCode,
	disabled = false,
}) {
	const [isOpen, setIsOpen] = useState(false);

	const handleFocus = () => setIsOpen(true);

	const handleBlur = () => setTimeout(() => setIsOpen(false), 100);

	const selectedCode = countryCode?.split(" ").pop();

	return (
		<div className="mt-0 flex rounded-md shadow-sm overflow-hidden border border-gray-300 focus-within:ring-2 focus-within:ring-indigo-600">
			<div className="relative">
				<select
					disabled={disabled}
					id={`${addOn.type}CountryCode`}
					name={`${addOn.type}CountryCode`}
					value={countryCode}
					onChange={onCountryCodeChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					className={`h-full appearance-none rounded-none border-0 text-sm text-transparent pr-8 pl-2 focus:ring-0 max-w-14 ${
						disabled ? "bg-neutral-200 text-gray-500" : "bg-neutral-100"
					}`}
				>
					{countries.map((_country) => (
						<option
							key={_country.name + _country.code}
							value={`${_country.name} ${_country.code}`}
							className="text-gray-900"
						>
							{_country.name} {_country.code}
						</option>
					))}
				</select>

				<div className="absolute inset-y-0 left-2 flex items-center text-sm text-gray-900 pointer-events-none">
					{isOpen ? countryCode : selectedCode}
				</div>
			</div>

			<input
				disabled={disabled}
				id={`${addOn.type}PhoneNumber`}
				name={`${addOn.type}PhoneNumber`}
				placeholder="Phone Number"
				type="tel"
				pattern="[0-9]{10}"
				title="Enter a valid 10-digit phone number"
				required
				value={phoneNumber}
				onChange={onPhoneNumberChange}
				className={`block w-full border-0 pl-3 pr-3 text-gray-900 text-sm placeholder:text-gray-400 focus:ring-0 ${
					disabled ? "bg-neutral-200 text-gray-500" : "bg-white"
				}`}
			/>
		</div>
	);
}

export default Notification;
